import Konva from 'konva'
import objectAssignDeep from 'object-assign-deep'
import RenMutator from './ren-mutator.es6'
import Geo from './geometrics.es6'
import Shapes from './shapes'
import getShapes from './shapes-mutator.es6'
import RenElementLabel from './ren-element-label.es6'

export default class RenElement extends Konva.Group {
  constructor (attrs, renAttrs, eventHandlers) {
    super(attrs)
    // Properties
    this._element = undefined
    this._assetData = undefined
    this._deviceTypes = undefined
    this._selected = false
    this._visualMode = undefined
    this.absoluteSizeScale = undefined
    this.renStage = undefined
    this.mutatorsLayer = undefined
    this.elementLabelsLayer = undefined
    Object.assign(this, renAttrs)
    // Event handlers (must bind this from _eventsParent)
    this.handleClick = eventHandlers.handleClick.bind(this._eventsParent)
    this.handleDragmove = eventHandlers.handleDragmove.bind(this._eventsParent)
    this.handleUpdate = eventHandlers.handleUpdate.bind(this._eventsParent)

    this.assetId = this._element.asset && this._element.asset.id
    this.diffData = Object.assign({}, attrs,
      {
        assetId: this._element.asset && this._element.asset.id,
        assetData: this._assetData,
        selected: this._selected,
        labelRotation: this._labelRotation,
        labelPosition: this._labelPosition,
        absoluteSizeScale: this.absoluteSizeScale
      })

    // Local properties
    this.renAnchors = []
    this.renWrap = undefined
    this.renHoverWrap = undefined
    this.renRotator = undefined
    this.renRotateVector = undefined
    this.otherSelectedRenElements = []
    this.shape = undefined
    this.interval = undefined

    // Update shapes and mutators + react to initial stage values
    this._updateAllShapes(this._assetData, this._deviceTypes)
    // this._updateAllMutators()
    this.reactToStageChanges()

    // Bind own events
    this.bindEvents()
  }

  bindEvents () {
    this.on('click.main tap.main', (e) => {
      console.log('EVT[ren-element]:click tap')
      if (this.renStage.addingElementMode) {
        // Pass the event to the renElementAdding object
        this.renStage.renElementAdding.onClick()
      } else if (this.renStage.selRectMode) {
        this.renStage.endSelectionMode()
      } else if (this.isClickable()) {
        // Handle here (renElement)
        this.handleClick(this._element.id, e.evt.ctrlKey) // if Ctrl pressed, toggle this in the existing selection
      }
      e.cancelBubble = true
    })

    this.on('dragstart', () => {
      console.log('EVT[ren-element]:dragstart')
      this.onDragstart()
    })

    this.on('dragmove', () => {
      this.onDragmove()
    })

    this.on('dragend', () => {
      console.log('EVT[ren-element]:dragend')
      this.onDragend()
    })
  }

  destroy () {
    // Destroy all mutators
    this._destroyAllMutators()
    this._destroyLabel()

    // Destroy
    super.destroy()
  }

  // GETTERS AND SETTERS
  set selected (value) {
    this._selected = value
    this.reactToStageChanges()
  }

  set visualMode (value) {
    this._visualMode = value
    this.reactToStageChanges()
  }

  // Apply the relevant changes before calling this method
  reactToStageChanges () {
    const visualMode = this._visualMode
    const selected = this._selected

    this.setAttr('draggable', visualMode === 'edit' && selected)

    // Bind conditional events
    if (this.isClickable()) {
      if (!this.eventListeners.mouseover) {
        this.on('mouseover', () => {
          this.onHoverChange(true)
        })
      }
      if (!this.eventListeners.mouseout) {
        this.on('mouseout', () => {
          this.onHoverChange(false)
        })
      }
    } else {
      this.off('mouseover')
      this.off('mouseout')
    }

    this._updateAllMutators()
    this._drawLabel()
  }

  // Returns the actual renElement points
  getPoints (snapToGrid = false) {
    let result = []
    if (this._element.pattern === 'node') {
      result = [this.attrs.x, this.attrs.y]
    } else {
      const renLine = [...this.children].find(shape => shape.attrs.name === 'line')
      if (renLine) {
        // Offset by the offsetted center of the renElement - Konva.Group
        // (used when dragging the entire renElement)
        result = renLine.attrs.points.map((p, index) => {
          return (index % 2 === 0) ? p + this.attrs.x : p + this.attrs.y
        })
      }
    }

    const { gridSpan, zoomLevel } = this.renStage
    return snapToGrid ? Geo.snapPointsToGrid(result, gridSpan, zoomLevel) : result
  }

  // Applies the value as new renElement points (not persistent)
  // And does layer batchDraw
  setPoints (value) {
    if (this._element.pattern === 'node') {
      this.x(value[0])
      this.y(value[1])
    } else {
      const renLine = [...this.children].find(shape => shape.attrs.name === 'line')
      if (renLine) {
        renLine.setAttr('points', value)
      }
    }
    // Draw the layer
    this.layerBatchDraw()
  }

  getDelta () { // Used when dragging (delta in position compared to original pos)
    const elementPoints = this._element.position.points // original
    const renPoints = this.getPoints() // now
    if (this._element.pattern === 'node') {
      return [
        renPoints[0] - elementPoints[0],
        renPoints[1] - elementPoints[1]
      ]
    } else if (this._element.pattern === 'vector' || this._element.pattern === 'area') {
      // renElement has original center always in [0, 0]
      return [
        this.attrs.x,
        this.attrs.y
      ]
    }
    return []
  }

  get pairedPoints () {
    return Geo.getPairedPoints(this.getPoints())
  }

  // Returns a modified set of element points (replacing the point at the idx position or appending at the end if idx is undefined)
  // If idx == -1, the last point gets updated
  // Updated points are snapped to grid
  getUpdatedPoints (newPoint, idx) {
    // Snap to grid
    const { gridSpan, zoomLevel } = this.renStage
    const snappedNewPoint = Geo.snapPointsToGrid(newPoint, gridSpan, zoomLevel)

    // Get exising points (paired) + applying the change if idx is in the range
    const newPairedPoints = this.pairedPoints.map((anchorPoint, i) => {
      return i === idx ? snappedNewPoint : anchorPoint
    })

    // Appending the newPoint in case idx is undefined
    if (idx === undefined) {
      newPairedPoints.push(snappedNewPoint)
    }

    // Changing the last point in case idx is -1
    if (idx === -1) {
      newPairedPoints[newPairedPoints.length - 1] = snappedNewPoint
    }

    // Spreading the paired points back to points (flat array)
    return Geo.getFlatPoints(newPairedPoints)
  }

  // Returns a modified set of element points (attrs similar to Array.splice)
  // Non-mutating
  // newPoint is snapped to grid
  getSplicedPoints (idx, deleteCount, newPoint) {
    // Snap to grid
    const { gridSpan, zoomLevel } = this.renStage
    const snappedNewPoint = newPoint && Geo.snapPointsToGrid(newPoint, gridSpan, zoomLevel)

    // Get exising points (paired)
    const pairedPoints = this.pairedPoints

    // Performing the splice
    const newPairedPoints = [...pairedPoints]
    if (!newPoint) {
      newPairedPoints.splice(idx, deleteCount)
    } else {
      newPairedPoints.splice(idx, deleteCount, snappedNewPoint)
    }

    // Spreading the paired points back to points (flat array)
    return Geo.getFlatPoints(newPairedPoints)
  }

  // Returns a modified set of points (moved by delta)
  // Delta passed in [x, y] format
  getOffsettedPoints (points, delta) {
    return points.map((p, index) => {
      return p + delta[index % 2] // 0 for even indexes (x), 1 for odd indexes (y)
    })
  }

  isClickable () {
    return this._visualMode === 'edit' || !!this._element.assetId
  }

  // METHODS

  isInBackground () {
    return !this.mutatorsLayer || !this.elementLabelsLayer
  }

  layerBatchDraw () {
    this.parent.batchDraw()
  }

  createShape () {
    const dt = this._element.deviceTyp // Device typ
    const attrs = {
      elementTypePattern: this._element.pattern,
      deviceTyp: this._element.deviceTyp,
      elementTypeName: this._element.elementTypeName,
      points: this._element.position.points,
      status: this._assetData,
      deviceTypes: this._deviceTypes,
      absoluteSizeScale: this.absoluteSizeScale,
      meta: this._element.meta
    }

    switch (dt) {
      case 'Generic':
        return new Shapes.Generic(attrs)
      case 'Discon':
        if (attrs.elementTypeName === 'Discon3') {
          return new Shapes.Discon3(attrs)
        } else {
          return new Shapes.Discon(attrs)
        }
      case 'Discon2':
        return new Shapes.Discon(attrs)
      case 'Elbox':
        return new Shapes.Elbox(attrs)
      case 'ControlRelay':
        return new Shapes.ControlRelay(attrs)
      case 'Panto':
        if (attrs.elementTypeName === 'PantoAB') {
          return new Shapes.PantoAB(attrs)
        } else {
          return new Shapes.PantoBA(attrs)
        }
      case 'Ksw':
        return new Shapes.Ksw(attrs)
      case 'Ksw2':
        return new Shapes.Ksw2(attrs)
      case 'Heating':
      case 'HeatingHostivar':
        console.log('Trying to insert heating!!!')
        return new Shapes.Heating(attrs)
      case 'Heater':
        return new Shapes.Heater(attrs)
      case 'HeatingSystem':
      case 'HeatingSystemCorrection':
        return new Shapes.HeatingSystem(attrs)
      case 'Mps':
        return new Shapes.Mps(attrs)
      case 'Pme':
        return new Shapes.Pme(attrs)
      case 'Ppi':
        if (attrs.elementTypeName === 'ChaPpi') {
          return new Shapes.ChaPpi(attrs)
        } else if (attrs.elementTypeName === 'ChaPpi2') {
          return new Shapes.ChaPpi2(attrs)
        } else if (attrs.elementTypeName === 'MelPpi') {
          return new Shapes.MelPpi(attrs)
        } else {
          return new Shapes.Ppi(attrs)
        }
      case 'Rcont':
        return new Shapes.Rcont(attrs)
      case 'Route':
        return new Shapes.Route(attrs)
      case 'Sg':
        if (attrs.elementTypeName === 'ChaSgStopCross') {
          return new Shapes.ChaSgStopCross(attrs)
        } else if (attrs.elementTypeName === 'ChaSgStopRed') {
          return new Shapes.ChaSgStopRed(attrs)
        } else if (attrs.elementTypeName === 'ChaSgThreeChambersPrepare') {
          return new Shapes.ChaSgThreeChambersPrepare(attrs)
        } else if (attrs.elementTypeName === 'ChaSgThreeChambersDirection') {
          return new Shapes.ChaSgThreeChambersDirection(attrs)
        } else if (attrs.elementTypeName === 'ChaSgTwoChambers') {
          return new Shapes.ChaSgTwoChambers(attrs)
        } else if (attrs.elementTypeName === 'ChaSgGo') {
          return new Shapes.ChaSgGo(attrs)
        } else if (attrs.elementTypeName === 'MelSgThreeChambersDirection') {
          return new Shapes.MelSgThreeChambersDirection(attrs)
        } else {
          return new Shapes.Sg(attrs)
        }
      case 'TbusSwitch':
        return new Shapes.TbusSwitch(attrs)
      case 'TbusTransceiver':
        return new Shapes.TbusTransceiver(attrs)
      case 'Tc':
        return new Shapes.Tc(attrs)
      case 'Twc':
        return new Shapes.Twc(attrs)
      case 'Vetra':
        return new Shapes.Vetra(attrs)
      case 'VoltageFreeLamp':
        return new Shapes.VoltageFreeLamp(attrs)
      case 'Zone':
        return new Shapes.Zone(attrs)
      default:
        if (this._element.elementTypeName === 'Text') {
          return new Shapes.Text(attrs)
        }
        if (!dt) {
          return new Shapes.Other(attrs)
        }
    }
  }

  _updateAllShapes (newVariables, deviceTypes) {
    // Destroy existing shapes
    // this.destroyChildren()

    // if shape already exist, apply new asset values to it
    if (this.shape) {
      // console.log("apply changes")
      this.shape.applyStatusOnShapes(newVariables, deviceTypes)
    // Create new shapes if it does not exist
    } else {
      this.shape = this.createShape()
      this.shape.renderShapes(newVariables, deviceTypes)
        .forEach(shape => this.add(shape))
    }
  }

  _updateAllMutators () {
    if (this.isInBackground()) {
      return
    }

    // Destroy existing
    this._destroyAllMutators()

    // Anchors - create new
    if (this._visualMode === 'edit' && this._selected) {
      this.pairedPoints.forEach((point, index) => {
        const renAnchor = new RenMutator.Anchor(
          {
            // Konva properties
            x: point[0],
            y: point[1],
            rotation: 0,
            draggable: true
          },
          {
            // Ren properties
            idx: index,
            _eventsParent: this,
            _scale: this.absoluteSizeScale
          },
          {
            // Ren events
            handleAnchorDragmove: this.onAnchorDragmove,
            handleAnchorDragend: this.onDragend
          }
        )

        // Bind locally + Add to the layer
        this.renAnchors.push(renAnchor)
        this.mutatorsLayer.add(renAnchor)
      })
    }

    // Wrap - create new
    if (this._visualMode !== 'edit' && this._selected) {
      const renWrap = new RenMutator.Wrap(
        {
          // Konva properties
          x: this.attrs.x,
          y: this.attrs.y,
          rotation: this.attrs.rotation
        },
        {
          // Ren properties
          _renElementShapes: this.children,
          _pattern: this._element.pattern,
          _elementTypeName: this._element.elementTypeName,
          _hover: false,
          _eventsParent: this,
          _scale: this.absoluteSizeScale
        },
        {
          // Ren events
        }
      )

      // Bind locally + Add to the layer
      this.renWrap = renWrap
      this.mutatorsLayer.add(renWrap)
    }

    // Rotator and RotateVector - create new
    if (this._visualMode === 'edit' && this._selected &&
      this.renStage.selectionIds.length === 1 &&
      this._element.pattern === 'node') {
      const distance = 40 / this.absoluteSizeScale
      const angledPosition = Geo.calcAngledPosition([this.attrs.x, this.attrs.y], distance, this.attrs.rotation)

      const renRotator = new RenMutator.Rotator(
        {
          // Konva properties
          x: angledPosition[0],
          y: angledPosition[1],
          rotation: 0,
          draggable: true
        },
        {
          // Ren properties
          _eventsParent: this,
          _scale: this.absoluteSizeScale
        },
        {
          // Ren events
          handleRotatorDragmove: this.onRotatorDragmove,
          handleRotatorDragend: this.onRotatorDragend
        }
      )

      const renRotateVector = new RenMutator.RotateVector(
        {
          // Konva properties
          x: this.attrs.x,
          y: this.attrs.y,
          rotation: this.attrs.rotation
        },
        {
          // Ren properties
          _scale: this.absoluteSizeScale
        }
      )

      // Bind locally + Add to the layer
      this.renRotator = renRotator
      this.renRotateVector = renRotateVector
      this.mutatorsLayer.add(renRotateVector, renRotator)
    }

    // Redraw the mutators layer
    this.mutatorsLayer.batchDraw()
  }

  onAnchorDragmove (newPoint, anchorIdx) {
    // Move locally only
    this.setPoints(this.getUpdatedPoints(newPoint, anchorIdx))
  }

  onRotatorDragmove (newPoint) {
    // Calculate new rotation angle
    const { gridSpan, zoomLevel } = this.renStage
    const point0 = [this.attrs.x, this.attrs.y]
    const point1 = Geo.snapPointsToGrid([this.renRotator.attrs.x, this.renRotator.attrs.y], gridSpan, zoomLevel)
    const angle = Geo.calcAngle(point0, point1)
    this.rotation(angle)

    // Rotate the RotateVector
    this.renRotateVector.rotation(angle)

    // Layer batch draw
    this.layerBatchDraw()
  }

  onRotatorDragend () {
    // Clone element to avoid mutation
    const newElement = objectAssignDeep({}, this._element)
    // Apply new rotation
    newElement.position.rotation = this.attrs.rotation
    // Handle
    this.handleUpdate(newElement)
  }

  onDragstart () {
    this._destroyAllMutators()
    this._destroyLabel()

    this.otherSelectedRenElements = this.renStage.selectedRenElements.filter(renEl => renEl._element.id !== this._element.id)
  }

  // Only used when the element is dragged (not by anchor)
  // Used for co-dragging also the other selected elements
  onDragmove () {
    const delta = this.getDelta()
    this.otherSelectedRenElements.forEach(renElement => {
      renElement.setPoints(renElement.getOffsettedPoints(renElement._element.position.points, delta))
    })
  }

  onDragend (draggedByAnchor = false) {
    // Clone element to avoid mutation
    const newElement = objectAssignDeep({}, this._element)

    // Apply new points on the cloned element + Snap to grid
    newElement.position.points = this.getPoints(true)

    // Handle
    if (!draggedByAnchor) {
      this.renStage.onSelectedElementsUpdate()
    } else {
      this.handleUpdate(newElement)
    }
  }

  onHoverChange (hover) { // Boolean
    if (this.isInBackground()) {
      return
    }

    if (hover) {
      // Hover Wrap - create new
      const renHoverWrap = new RenMutator.Wrap(
        {
          // Konva properties
          x: this.attrs.x,
          y: this.attrs.y,
          rotation: this.attrs.rotation
        },
        {
          // Ren properties
          _renElementShapes: this.children,
          _pattern: this._element.pattern,
          _elementTypeName: this._element.elementTypeName,
          _hover: true,
          _eventsParent: this,
          _scale: this.absoluteSizeScale
        },
        {
          // Ren events
        }
      )

      // Bind locally + Add to the layer
      this.renHoverWrap = renHoverWrap
      this.mutatorsLayer.add(renHoverWrap)
      // Redraw the mutators layer
      this.mutatorsLayer.batchDraw()
    } else {
      // Hover Wrap - destroy
      this.renHoverWrap && this.renHoverWrap.destroy()
      this.renHoverWrap = undefined
      // Redraw the mutators layer
      this.mutatorsLayer.batchDraw()
    }
  }

  _destroyAllMutators () {
    if (this.isInBackground()) {
      return
    }

    this.renAnchors.forEach(renAnchor => renAnchor.destroy())
    this.renWrap && this.renWrap.destroy()
    this.renHoverWrap && this.renHoverWrap.destroy()
    this.renRotator && this.renRotator.destroy()
    this.renRotateVector && this.renRotateVector.destroy()
    this.mutatorsLayer.batchDraw()
    // Reset local pointers (for garbage collector to collect the objects)
    this.renAnchors = []
    this.renWrap = undefined
    this.renHoverWrap = undefined
    this.renRotator = undefined
    this.renRotateVector = undefined
  }

  _drawLabel () {
    const { asset } = this._element
    // hide label for heater
    // TODO make it configurable on element itself
    if (!this.isInBackground() && asset && asset.deviceType !== 'Heater') {
      this._destroyLabel()

      const isPositioned = this._element.pattern !== 'node'
      const wrapRect = this._calculateWrapRect(isPositioned)
      const { rotation } = this.attrs
      const labelRotation = this._labelRotation

      const { x, y } = this.shape.getLabelPoint(wrapRect, rotation, labelRotation)
      const text = asset.name
      const selected = this._selected
      const visualMode = this._visualMode
      const absoluteSizeScale = this.absoluteSizeScale || 1
      this.label = new RenElementLabel({
        x,
        y,
        rotation,
        labelRotation,
        text,
        selected,
        visualMode,
        isPositioned,
        absoluteSizeScale,
        setLabelRotation: this._setLabelRotation.bind(this),
        setLabelPosition: this._setLabelPosition.bind(this),
        changeHover: this.onHoverChange.bind(this),
        selectElement: (event) => this.handleClick(this._element.id, event.evt.ctrlKey)
      })
      this.elementLabelsLayer.add(this.label)
      this.elementLabelsLayer.batchDraw()
    }
  }

  _calculateWrapRect (isPositioned) {
    if (isPositioned) {
      const { x, y } = this._labelPosition || { x: 0, y: 0 }
      return { x, y, width: 0, height: 0 }
    }
    const { children, absoluteSizeScale } = this
    const wrapShapes = getShapes.wrap(children, true, absoluteSizeScale)
    const { x, y, width, height } = wrapShapes[0].attrs
    return { x, y, width, height }
  }

  _destroyLabel () {
    if (!this.isInBackground() && this.label) {
      this.label.destroy()
      this.elementLabelsLayer.batchDraw()
      this.label = null
    }
  }

  _updateLabel (fn) {
    const newElement = objectAssignDeep({}, this._element)
    if (!newElement.position.label) {
      newElement.position.label = {}
    }
    fn(newElement.position.label)
    this.handleUpdate(newElement)
  }

  _setLabelRotation (rotation) {
    this._updateLabel(label => {
      label.rotation = rotation
    })
  }

  _setLabelPosition (position) {
    const [x = 0, y = 0] = this.shape.points
    const relative = { x: position.x - x, y: position.y - y }
    this._updateLabel(label => {
      label.position = relative
    })
  }
}
